import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { buttonSizeClasses } from '@/app/components/Button'
import { Media } from '@/app/components/Media'
import PortableText from '@/app/components/PortableText'
import Video, { hasVideo } from '@/app/components/Video'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import {
  cn,
  ptPurpleTextParagraphs,
  ptWhiteTextHeaders,
  ptWhiteTextParagraphs,
} from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'
import { HeroBanner as HeroBannerProps } from '@/sanity.types'

type HeroBannerSectionProps = {
  block: HeroBannerProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export default function HeroBannerSection({ block, pageId, pageType }: HeroBannerSectionProps) {
  const theme = stegaClean(block.theme) || 'green'
  const decor = stegaClean(block.decor) || 'bars'

  const showVideo = hasVideo(block.video)
  const mediaType = stegaClean(block.mediaType)
  const showImage = mediaType === 'image' && block.image

  // Theme-specific background colors
  // cant use bg-green-500 because white paragraph wont meet contrast ratio requirements
  const themeBackgroundClass = theme === 'purple' ? 'bg-purple-700' : 'bg-[#208651]'

  const contentElement = block?.content?.length && (
    <PortableText
      className={cn(
        'flex flex-col items-center justify-center text-center mx-auto lg:max-w-[848px]',
        mediaType === 'image' && 'md:text-left md:items-start',
        ptWhiteTextHeaders,
        theme === 'purple' ? ptPurpleTextParagraphs : ptWhiteTextParagraphs,
      )}
      tracking={{ location: TRACKING_LOCATIONS.HERO }}
      buttonClassName={buttonSizeClasses({ lg: 'md' })}
      value={block.content as PortableTextBlock[]}
      documentId={pageId}
      documentType={pageType}
      parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
      paragraphClassName="size-body-sm lg:size-body-md"
      componentOverrides={{
        block: {
          'h2': ({ children }) => {
            const id = generateElementId(children)
            return (
              <h1
                id={id}
                data-heading-id={id}
                data-anchor-expose="true"
                className="size-header-h4 lg:size-header-h2"
              >
                {children}
              </h1>
            )
          },
          'body-md': ({ children }) => {
            const id = generateElementId(children)
            return (
              <h2
                id={id}
                data-heading-id={id}
                data-anchor-expose="true"
                className={cn(
                  'size-body-sm lg:size-body-md',
                  theme === 'purple' ? 'text-purple-200' : 'text-white-default',
                )}
              >
                {children}
              </h2>
            )
          },
        },
      }}
    />
  )

  return (
    <>
      <div className={cn('content-container content-container-wide relative overflow-hidden')}>
        <div
          className={cn(
            'block relative overflow-hidden z-10 max-w-[1240px] my-4xl mx-auto px-2xl py-6xl rounded-lg lg:p-10xl',
            themeBackgroundClass,
          )}
        >
          {/* Decorative bars pattern - desktop only */}
          {decor === 'bars' && (
            <div
              className="hidden lg:block absolute top-[100px] right-[-300px] w-[855px] h-[480px] pointer-events-none z-0"
              style={{ transform: 'rotate(153deg)' }}
              aria-hidden="true"
            >
              <div className="grid grid-cols-3 w-full h-full">
                {Array.from({ length: 30 }).map((_, index) => {
                  // Indices of tiles that should be colored
                  const coloredTiles = new Set([0, 4, 6, 8, 10, 12, 14, 16, 18, 22, 28])
                  const shouldShowColor = coloredTiles.has(index)
                  const bgColor = theme === 'purple' ? 'bg-purple-500' : 'bg-green-400'
                  return (
                    <div
                      key={index}
                      className={cn(
                        'w-[285px] h-[48px]',
                        shouldShowColor ? bgColor : 'bg-transparent',
                      )}
                    />
                  )
                })}
              </div>
            </div>
          )}

          {/* Decorative grid pattern - desktop only */}
          {decor === 'grid' && (
            <div
              className="hidden lg:block absolute top-1/2 w-[618px] h-[617px] pointer-events-none z-0 bg-no-repeat bg-center"
              style={{
                right: '-5%',
                transform: 'translateY(-50%)',
                backgroundImage: 'url(/images_pwf/hero_banner_grid.png)',
                backgroundSize: '618px 617px',
              }}
              aria-hidden="true"
            />
          )}

          <div className="relative z-10">
            {showImage ? (
              <div className="mx-auto flex max-w-[528px] flex-col gap-6xl md:max-w-[1120px] md:flex-row md:items-center md:gap-6xl lg:gap-3xl xl:max-w-none">
                <div className="flex-1 lg:max-w-[848px] xl:basis-[calc(50%+var(--spacing-4xl))] xl:shrink-0">
                  {contentElement}
                </div>
                <div className="flex-1 lg:max-w-[490px] xl:basis-[490px]">
                  <Media
                    image={block.image}
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 490px"
                  />
                </div>
              </div>
            ) : (
              contentElement
            )}
            {showVideo && (
              <div className="mt-3xl md:mt-5xl lg:mt-7xl mx-auto max-w-[1120px]">
                <Video video={block.video} className="rounded-md lg:rounded-lg overflow-hidden" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
